import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  CategoryAPIResponse,
  Category,
  CategoryTreeItem,
  CategoryDropdownItem,
  ParentCategoryItem,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  ListCategoriesRequest,
  CategoryListResponse,
  CategoryTreeResponse,
  DeleteCategoryResponse,
  CategoryCacheHeaders
} from '@/types/category.types';

/**
 * Category Service
 * Provides comprehensive category management functionality including
 * hierarchical structures, caching, and webhook notifications.
 * 
 * @since SDK v5.0.58
 */
class CategoryService {
  // ==========================================
  // CRUD OPERATIONS
  // ==========================================

  /**
   * Create a new category with hierarchical support
   * @param categoryData - Category creation data
   * @returns Created category
   */
 // In your category.service.ts file

async createCategory(categoryData: CreateCategoryRequest): Promise<Category> {
  try {
    const response = await apiClient.post<CategoryAPIResponse<Category>>(
      API_ENDPOINTS.CATEGORIES.CREATE, 
      categoryData
    );
    
    if (!response.data.success) {
      throw response.data;
    }

    return response.data.data;

  } catch (error: any) {
    throw error;
  }
}

  /**
   * Get category by ID
   * @param id - Category ID
   * @returns Category details
   */
  async getCategoryById(id: string): Promise<Category> {
    try {
      const response = await apiClient.get<CategoryAPIResponse<Category>>(
        API_ENDPOINTS.CATEGORIES.GET_BY_ID(id)
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch category');
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Category with ID ${id} not found`);
      }
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  /**
   * Update existing category
   * @param id - Category ID
   * @param categoryData - Partial category data to update
   * @returns Updated category
   */
  async updateCategory(id: string, categoryData: UpdateCategoryRequest): Promise<Category> {
    try {
      const response = await apiClient.put<CategoryAPIResponse<Category>>(
        API_ENDPOINTS.CATEGORIES.UPDATE(id), 
        categoryData
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update category');
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Category with ID ${id} not found`);
      }
      if (error.response?.status === 409) {
        throw new Error(`Category name "${categoryData.category_name}" already exists`);
      }
      console.error('Error updating category:', error);
      throw error;
    }
  }

  /**
   * Soft delete a category
   * @param id - Category ID
   * @returns Deletion result
   */
  async deleteCategory(id: string): Promise<boolean> {
    try {
      const response = await apiClient.delete<DeleteCategoryResponse>(
        API_ENDPOINTS.CATEGORIES.DELETE(id)
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete category');
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(`Category with ID ${id} not found`);
      }
      if (error.response?.status === 409) {
        throw new Error('Category is linked to existing courses and cannot be deleted');
      }
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  // ==========================================
  // TREE AND HIERARCHY OPERATIONS
  // ==========================================

  /**
   * Get complete category tree structure with caching support
   * @param cacheHeaders - Optional cache headers for conditional requests
   * @returns Category tree structure
   */
  async getCategoryTree(cacheHeaders?: CategoryCacheHeaders): Promise<CategoryTreeResponse> {
    try {
      const headers: any = {};
      if (cacheHeaders?.['If-Modified-Since']) {
        headers['If-Modified-Since'] = cacheHeaders['If-Modified-Since'];
      }
      if (cacheHeaders?.['If-Unmodified-Since']) {
        headers['If-Unmodified-Since'] = cacheHeaders['If-Unmodified-Since'];
      }

      const response = await apiClient.get<CategoryAPIResponse<CategoryTreeResponse>>(
        API_ENDPOINTS.CATEGORIES.TREE,
        { headers }
      );
      
      if (response.status === 304) {
        // Not modified - use cached version
        throw new Error('NOT_MODIFIED');
      }
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch category tree');
    } catch (error: any) {
      if (error.message === 'NOT_MODIFIED' || error.response?.status === 304) {
        throw new Error('NOT_MODIFIED');
      }
      if (error.response?.status === 412) {
        throw new Error('PRECONDITION_FAILED');
      }
      console.error('Error fetching category tree:', error);
      throw error;
    }
  }

  /**
   * Get all parent categories (root level)
   * @returns List of parent categories
   */
  async getParentCategories(): Promise<ParentCategoryItem[]> {
    try {
      const response = await apiClient.get<CategoryAPIResponse<ParentCategoryItem[]>>(
        API_ENDPOINTS.CATEGORIES.PARENT_CATEGORIES
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch parent categories');
    } catch (error) {
      console.error('Error fetching parent categories:', error);
      throw error;
    }
  }

  // ==========================================
  // LIST AND SEARCH OPERATIONS
  // ==========================================

  /**
   * Get paginated list of categories with search
   * @param filters - Search and pagination filters
   * @returns Paginated category list
   */
  async listCategories(filters?: ListCategoriesRequest): Promise<CategoryListResponse> {
    try {
      const response = await apiClient.post<CategoryAPIResponse<CategoryListResponse>>(
        API_ENDPOINTS.CATEGORIES.LIST,
        {
          skip: filters?.skip || 0,
          limit: filters?.limit || 10,
          category_name: filters?.category_name
        }
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch categories');
    } catch (error) {
      console.error('Error fetching category list:', error);
      throw error;
    }
  }

  /**
   * Get simplified category list for dropdowns
   * @returns List of categories for dropdown
   */
  async getCategoryDropdown(): Promise<CategoryDropdownItem[]> {
    try {
      const response = await apiClient.get<CategoryAPIResponse<CategoryDropdownItem[]>>(
        API_ENDPOINTS.CATEGORIES.DROPDOWN
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch category dropdown');
    } catch (error) {
      console.error('Error fetching category dropdown:', error);
      throw error;
    }
  }

  /**
   * Get simplified parent category list for dropdowns
   * @returns List of parent categories for dropdown
   */
  async getParentCategoryDropdown(): Promise<CategoryDropdownItem[]> {
    try {
      const response = await apiClient.get<CategoryAPIResponse<CategoryDropdownItem[]>>(
        API_ENDPOINTS.CATEGORIES.PARENT_DROPDOWN
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch parent category dropdown');
    } catch (error) {
      console.error('Error fetching parent category dropdown:', error);
      throw error;
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Validate if category name is unique
   * @param categoryName - Category name to validate
   * @returns True if unique, false otherwise
   */
  async validateCategoryName(categoryName: string): Promise<boolean> {
    try {
      const result = await this.listCategories({ 
        category_name: categoryName,
        limit: 1 
      });
      return result.total_count === 0;
    } catch (error) {
      console.error('Error validating category name:', error);
      return false;
    }
  }

  /**
   * Find categories by partial name match
   * @param searchTerm - Search term
   * @param limit - Maximum results to return
   * @returns Matching categories
   */
  async searchCategories(searchTerm: string, limit: number = 10): Promise<Category[]> {
    try {
      const result = await this.listCategories({
        category_name: searchTerm,
        limit
      });
      return result.categories;
    } catch (error) {
      console.error('Error searching categories:', error);
      return [];
    }
  }

  /**
   * Get category hierarchy path
   * @param categoryId - Category ID
   * @returns Array of categories from root to specified category
   */
  async getCategoryPath(categoryId: string): Promise<Category[]> {
    try {
      const path: Category[] = [];
      let currentId: string | null = categoryId;
      
      while (currentId && currentId !== 'root') {
        const category = await this.getCategoryById(currentId);
        path.unshift(category);
        currentId = category.parent_category_id === 'root' ? null : category.parent_category_id;
      }
      
      return path;
    } catch (error) {
      console.error('Error getting category path:', error);
      return [];
    }
  }

  /**
   * Check if category has child categories
   * @param categoryId - Category ID
   * @returns True if has children, false otherwise
   */
  async hasChildCategories(categoryId: string): Promise<boolean> {
    try {
      const tree = await this.getCategoryTree();
      
      const findCategory = (categories: CategoryTreeItem[]): boolean => {
        for (const cat of categories) {
          if (cat._id === categoryId) {
            return (cat.categories?.length || 0) > 0;
          }
          if (cat.categories && findCategory(cat.categories)) {
            return true;
          }
        }
        return false;
      };
      
      return findCategory(tree.saved_category_tree);
    } catch (error) {
      console.error('Error checking child categories:', error);
      return false;
    }
  }

  /**
   * Build flat list from category tree
   * @param includeInactive - Include inactive categories
   * @returns Flat list of all categories
   */
  async getFlatCategoryList(includeInactive: boolean = false): Promise<Category[]> {
    try {
      const tree = await this.getCategoryTree();
      const flatList: Category[] = [];
      
      const flatten = (categories: CategoryTreeItem[]) => {
        for (const cat of categories) {
          if (includeInactive || cat.is_active) {
            flatList.push(cat);
          }
          if (cat.categories) {
            flatten(cat.categories);
          }
        }
      };
      
      flatten(tree.saved_category_tree);
      return flatList;
    } catch (error) {
      console.error('Error getting flat category list:', error);
      return [];
    }
  }
}

// Export singleton instance
export const categoryService = new CategoryService();